import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Clock,
  FileText,
  Pencil,
  Phone,
  Plus,
  StickyNote,
} from 'lucide-react';
import { PageHeader, ErrorState, TableSkeleton } from '@/components/shared/page';
import { Can } from '@/components/shared/can';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Avatar, Select, Separator, Textarea } from '@/components/ui/misc';
import { formatDate, formatDateTime, formatMoney, timeAgo } from '@/lib/format';
import { useDeal, useLogActivity, usePipelines, useSaveTask, useTimeline } from './api';
import {
  DEAL_TYPE_LABELS,
  LEAD_SOURCE_LABELS,
  QUOTE_STATUS_LABELS,
  QUOTE_STATUS_VARIANT,
  TASK_PRIORITY_VARIANT,
  TASK_TYPE_LABELS,
} from './labels';
import { DealDialog } from './deal-dialog';
import { MoveDealDialog } from './move-deal-dialog';
import type { Deal, DealStageHistoryEntry, PipelineStage, QuoteStatus } from './types';

type DealDetail = Deal & {
  stageHistory: DealStageHistoryEntry[];
  quotes: {
    id: string;
    number: string;
    version: number;
    status: QuoteStatus;
    grandTotal: string | null;
    currency: string;
    issueDate: string;
    validUntil: string | null;
  }[];
};

export function DealDetailPage() {
  const { dealId } = useParams<{ dealId: string }>();
  const { data, isLoading, error, refetch } = useDeal(dealId);
  const { data: pipelines = [] } = usePipelines();
  const [editing, setEditing] = useState(false);
  const [movingTo, setMovingTo] = useState<PipelineStage | null>(null);

  if (isLoading) {
    return (
      <div>
        <PageHeader title="Deal" />
        <TableSkeleton />
      </div>
    );
  }
  if (error || !data) {
    return <ErrorState message="Could not load this deal." onRetry={() => void refetch()} />;
  }

  const deal = data as DealDetail;
  const stages = pipelines.find((pipeline) => pipeline.id === deal.pipelineId)?.stages ?? [];

  return (
    <div>
      <PageHeader
        title={deal.title}
        breadcrumbs={[
          { label: 'Pipeline', to: '/crm/deals' },
          { label: deal.company.name, to: `/crm/companies` },
          { label: deal.number },
        ]}
        description={`${DEAL_TYPE_LABELS[deal.dealType]} · ${deal.pipeline.name}`}
        actions={
          <Can permission="crm.deal.update">
            <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
              <Pencil className="h-4 w-4" />
              Edit
            </Button>
          </Can>
        }
      />

      {deal.status !== 'OPEN' && (
        <div
          className={
            deal.status === 'WON'
              ? 'mb-5 flex items-center gap-2 rounded-md border border-emerald-300 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-900'
              : 'mb-5 flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-2.5 text-sm text-destructive'
          }
        >
          <CheckCircle2 className="h-4 w-4 shrink-0" />
          {deal.status === 'WON' ? (
            <span>
              Won on {formatDate(deal.actualCloseDate)} for{' '}
              {formatMoney(deal.value, deal.currency)}
            </span>
          ) : (
            <span>
              Lost on {formatDate(deal.actualCloseDate)} — {deal.lostReason}
              {deal.competitor ? ` (to ${deal.competitor})` : ''}
              {deal.lostNote ? ` · ${deal.lostNote}` : ''}
            </span>
          )}
        </div>
      )}

      {deal.isRotting && deal.status === 'OPEN' && (
        <div className="mb-5 flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm text-amber-900">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          No activity since {timeAgo(deal.lastActivityAt)} — past the threshold for{' '}
          {deal.stage.name}.
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-[1fr_20rem]">
        <div className="space-y-5">
          {deal.status === 'OPEN' && stages.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Stage</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-1.5">
                  {stages.map((stage) => (
                    <button
                      key={stage.id}
                      disabled={stage.id === deal.stageId}
                      onClick={() => setMovingTo(stage)}
                      className={
                        stage.id === deal.stageId
                          ? 'rounded-full px-3 py-1 text-xs font-medium text-white'
                          : 'rounded-full border px-3 py-1 text-xs transition-colors hover:bg-accent'
                      }
                      style={stage.id === deal.stageId ? { backgroundColor: stage.colour } : {}}
                    >
                      {stage.name}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <TimelinePanel entityType="deal" entityId={deal.id} />

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <FileText className="h-4 w-4" />
                Quotes
              </CardTitle>
            </CardHeader>
            <CardContent>
              {deal.quotes.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No quotes yet.{' '}
                  <Can permission="crm.quote.create">
                    <Link
                      to={`/crm/quotes/new?dealId=${deal.id}&companyId=${deal.companyId}`}
                      className="text-primary hover:underline"
                    >
                      Build one
                    </Link>
                  </Can>
                </p>
              ) : (
                <ul className="space-y-2">
                  {deal.quotes.map((quote) => (
                    <li key={quote.id}>
                      <Link
                        to={`/crm/quotes/${quote.id}`}
                        className="flex items-center justify-between rounded-md border px-3 py-2 transition-colors hover:bg-accent"
                      >
                        <span className="text-sm">
                          {quote.number}
                          {quote.version > 1 && (
                            <span className="text-muted-foreground"> v{quote.version}</span>
                          )}
                        </span>
                        <span className="flex items-center gap-3">
                          <span className="tabular text-sm">
                            {formatMoney(quote.grandTotal, quote.currency)}
                          </span>
                          <Badge variant={QUOTE_STATUS_VARIANT[quote.status]}>
                            {QUOTE_STATUS_LABELS[quote.status]}
                          </Badge>
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Clock className="h-4 w-4" />
                Stage history
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ol className="space-y-3">
                {deal.stageHistory.map((entry) => (
                  <li key={entry.id} className="flex items-start gap-3 text-sm">
                    <span
                      className="mt-1.5 h-2 w-2 shrink-0 rounded-full"
                      style={{ backgroundColor: entry.toStage.colour }}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-1.5">
                        {entry.fromStage && (
                          <>
                            <span className="text-muted-foreground">{entry.fromStage.name}</span>
                            <ArrowRight className="h-3 w-3 text-muted-foreground" />
                          </>
                        )}
                        <span className="font-medium">{entry.toStage.name}</span>
                        {entry.durationSeconds !== null && (
                          <span className="text-xs text-muted-foreground">
                            after {formatDuration(entry.durationSeconds)}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {formatDateTime(entry.changedAt)}
                        {entry.changedBy ? ` · ${entry.changedBy.name}` : ''}
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-5">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <Detail label="Value" value={formatMoney(deal.value, deal.currency)} strong />
              {deal.recurringValue && (
                <Detail
                  label="Recurring"
                  value={`${formatMoney(deal.recurringValue, deal.currency)}${deal.billingCycle ? ` / ${deal.billingCycle.toLowerCase()}` : ''}`}
                />
              )}
              <Detail
                label="Weighted"
                value={formatMoney(deal.weightedValue, deal.currency)}
              />
              <Detail
                label="Probability"
                value={`${deal.probability ?? deal.stage.probability}%`}
              />
              <Detail label="Expected close" value={formatDate(deal.expectedCloseDate)} />
              <Separator />
              <Detail label="Company" value={deal.company.name} />
              <Detail
                label="Contact"
                value={
                  deal.contact
                    ? [deal.contact.firstName, deal.contact.lastName].filter(Boolean).join(' ')
                    : '—'
                }
              />
              <Detail label="Source" value={deal.source ? LEAD_SOURCE_LABELS[deal.source] : '—'} />
              <Separator />
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Owner</span>
                {deal.owner ? (
                  <span className="flex items-center gap-1.5">
                    <Avatar name={deal.owner.name} size={20} />
                    <span className="text-sm">{deal.owner.name}</span>
                  </span>
                ) : (
                  <span className="text-sm text-muted-foreground">Unassigned</span>
                )}
              </div>
              <Detail label="Created" value={formatDate(deal.createdAt)} />
            </CardContent>
          </Card>
        </div>
      </div>

      {editing && <DealDialog deal={deal} onClose={() => setEditing(false)} />}
      {movingTo && (
        <MoveDealDialog
          deal={deal}
          targetStage={movingTo}
          currentStage={stages.find((stage) => stage.id === deal.stageId)}
          onClose={() => setMovingTo(null)}
        />
      )}
    </div>
  );
}

/** Notes, calls, meetings and tasks against one record (spec §5.6). */
export function TimelinePanel({
  entityType,
  entityId,
}: {
  entityType: string;
  entityId: string;
}) {
  const { data } = useTimeline(entityType, entityId);
  const log = useLogActivity();
  const saveTask = useSaveTask();

  const [mode, setMode] = useState<'note' | 'call' | 'task'>('note');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [outcome, setOutcome] = useState('connected');
  const [duration, setDuration] = useState('');
  const [dueAt, setDueAt] = useState('');

  const submit = async () => {
    if (title.trim().length === 0) return;

    if (mode === 'task') {
      await saveTask.mutateAsync({ title, description: body, entityType, entityId, dueAt, type: 'FOLLOW_UP' });
    } else {
      await log.mutateAsync({
        entityType,
        entityId,
        type: mode === 'call' ? 'CALL' : 'NOTE',
        title,
        body,
        ...(mode === 'call'
          ? { meta: { outcome, durationMinutes: duration ? Number(duration) : undefined } }
          : {}),
      });
    }
    setTitle('');
    setBody('');
    setDuration('');
    setDueAt('');
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Activity</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-md border p-3">
          <div className="mb-2 flex gap-1">
            {(
              [
                ['note', 'Note', StickyNote],
                ['call', 'Log a call', Phone],
                ['task', 'Task', Plus],
              ] as const
            ).map(([value, label, Icon]) => (
              <button
                key={value}
                onClick={() => setMode(value)}
                className={
                  mode === value
                    ? 'flex items-center gap-1.5 rounded-md bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground'
                    : 'flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs text-muted-foreground hover:bg-accent'
                }
              >
                <Icon className="h-3 w-3" />
                {label}
              </button>
            ))}
          </div>

          <Input
            value={title}
            placeholder={
              mode === 'call'
                ? 'What was discussed?'
                : mode === 'task'
                  ? 'What needs doing?'
                  : 'Add a note'
            }
            onChange={(event) => setTitle(event.target.value)}
          />

          {mode === 'call' && (
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              <Select value={outcome} onChange={(event) => setOutcome(event.target.value)}>
                <option value="connected">Connected</option>
                <option value="no_answer">No answer</option>
                <option value="busy">Busy</option>
                <option value="wrong_number">Wrong number</option>
                <option value="callback">Callback requested</option>
              </Select>
              <Input
                type="number"
                min={0}
                placeholder="Minutes"
                value={duration}
                onChange={(event) => setDuration(event.target.value)}
              />
            </div>
          )}

          {mode === 'task' && (
            <div className="mt-2 space-y-1.5">
              <Label className="text-xs">Due</Label>
              <Input
                type="datetime-local"
                value={dueAt}
                onChange={(event) => setDueAt(event.target.value)}
              />
            </div>
          )}

          <Textarea
            className="mt-2"
            rows={2}
            value={body}
            placeholder="More detail (optional)"
            onChange={(event) => setBody(event.target.value)}
          />

          <Button
            size="sm"
            className="mt-2"
            onClick={submit}
            disabled={title.trim().length === 0 || log.isPending || saveTask.isPending}
          >
            {log.isPending || saveTask.isPending ? 'Saving…' : 'Add'}
          </Button>
        </div>

        {data?.tasks.filter((task) => task.status !== 'DONE').length ? (
          <div>
            <div className="mb-1.5 text-xs font-medium text-muted-foreground">Open tasks</div>
            <ul className="space-y-1.5">
              {data.tasks
                .filter((task) => task.status !== 'DONE')
                .map((task) => (
                  <li
                    key={task.id}
                    className="flex items-center justify-between rounded-md border px-3 py-2"
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-sm">{task.title}</span>
                      <span className="block text-xs text-muted-foreground">
                        {TASK_TYPE_LABELS[task.type]}
                        {task.dueAt ? ` · due ${formatDate(task.dueAt)}` : ''}
                      </span>
                    </span>
                    <span className="flex items-center gap-2">
                      <Badge variant={TASK_PRIORITY_VARIANT[task.priority]} className="text-[10px]">
                        {task.priority}
                      </Badge>
                      <button
                        onClick={() => saveTask.mutate({ id: task.id, status: 'DONE' })}
                        className="text-xs text-muted-foreground hover:text-foreground hover:underline"
                      >
                        Done
                      </button>
                    </span>
                  </li>
                ))}
            </ul>
          </div>
        ) : null}

        <ol className="space-y-3">
          {(data?.activities ?? []).map((activity) => (
            <li key={activity.id} className="flex items-start gap-2.5">
              <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground/40" />
              <div className="min-w-0 flex-1">
                <div className="text-sm">{activity.title}</div>
                {activity.body && (
                  <p className="mt-0.5 whitespace-pre-wrap text-sm text-muted-foreground">
                    {activity.body}
                  </p>
                )}
                <div className="mt-0.5 text-xs text-muted-foreground">
                  {activity.actor?.name ?? 'System'} · {timeAgo(activity.occurredAt)}
                </div>
              </div>
            </li>
          ))}
          {data?.activities.length === 0 && (
            <li className="text-sm text-muted-foreground">Nothing logged yet.</li>
          )}
        </ol>
      </CardContent>
    </Card>
  );
}

function Detail({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={strong ? 'tabular font-semibold' : 'tabular text-sm'}>{value}</span>
    </div>
  );
}

function formatDuration(seconds: number): string {
  const days = Math.floor(seconds / 86400);
  if (days > 0) return `${days} ${days === 1 ? 'day' : 'days'}`;
  const hours = Math.floor(seconds / 3600);
  if (hours > 0) return `${hours}h`;
  return `${Math.max(1, Math.floor(seconds / 60))}m`;
}
